import { useStore } from 'jotai';
import { useCallback } from 'react';
import { useToastMessageState } from '../../../../../components/ToastMessage/ToastMessageProvider';
import { ManageCompanyFieldsTypes } from '../../../../../schemas/template-assignment-schema';
import {
  useUpdateCompanyAndState,
  useUpdateCompanyStates,
} from '../../../../../services/hooks/useUpdateCompanyAndState';
import { fieldsMappingByCompanyState } from '../../../../../services/state/CompanyProvidersState';
import { partialEqualsIgnoreUnsetValues } from '../../../../../util/partialEquals';
import { IKpiCompanyData } from '../AllCompaniesData';
import { useDomainEdit } from './useDomainEdit';

type UseManageCompanySubmitArgs = {
  companyId: number;
  defaultValues: ManageCompanyFieldsTypes;
  isConsolidatedV2: boolean;
  isOnboarded: boolean;
};

export function useManageCompanySubmit({
  companyId,
  defaultValues,
  isConsolidatedV2,
  isOnboarded,
}: UseManageCompanySubmitArgs) {
  const editDomain = useDomainEdit();
  const updateCompanyAndState = useUpdateCompanyAndState();
  const refreshCompanyState = useUpdateCompanyStates();
  const { pushErrorToast } = useToastMessageState();
  const store = useStore();

  return useCallback(
    async (data: ManageCompanyFieldsTypes): Promise<boolean> => {
      // `defaultValues.domain` doesn't pass through yup, so trim defensively for the diff.
      const initialDomain = (defaultValues.domain ?? '').trim();
      const nextDomain = data.domain ?? '';
      const shouldUpdateDomain = isConsolidatedV2 && nextDomain !== initialDomain;

      let domainEditFailed = false;
      if (shouldUpdateDomain) {
        const providers = await store.get(fieldsMappingByCompanyState(companyId));
        const primaryRecord = Array.isArray(providers)
          ? (providers.find((f) => f.provider === 'website') ?? null)
          : null;
        if (!primaryRecord) {
          // Should be unreachable
          console.error('No primary website provider record found for company', { companyId });
          pushErrorToast({
            message: 'Could not update domain — no primary website mapping for this company.',
          });
          domainEditFailed = true;
        } else {
          const ok = await editDomain({
            providerRecordId: primaryRecord.id,
            companyId,
            newDomain: nextDomain,
          });
          if (!ok) {
            domainEditFailed = true;
          }
        }
      }

      const companyUpdates: Partial<ManageCompanyFieldsTypes> = {
        fye: data.fye,
        adjustHistoricKPIs: data.adjustHistoricKPIs,
      };
      // Onboarded companies manage respondents via the dedicated suggest/withdraw endpoints, so keep
      // them out of the full-list PUT (which would otherwise race the diff-gated respondent sync).
      if (!isOnboarded) {
        companyUpdates.respondents = data.respondents;
        companyUpdates.secondaryRespondents = data.secondaryRespondents;
      }
      const otherFieldsChanged = !partialEqualsIgnoreUnsetValues(companyUpdates, defaultValues);

      let companyUpdateFailed = false;
      if (otherFieldsChanged) {
        const updated = await updateCompanyAndState(
          companyId,
          companyUpdates as Partial<IKpiCompanyData>,
          /* showSuccessMessage */ !domainEditFailed,
          /* optimisticallyUpdateCompanyMetrics */ false,
          /* awaitBeforeUpdate */ isConsolidatedV2
        );
        // undefined = caught error (toast already pushed by useUpdateCompanyAndState).
        if (updated === undefined) {
          companyUpdateFailed = true;
        }
      }

      const domainSucceeded = shouldUpdateDomain && !domainEditFailed;
      const companySucceeded = otherFieldsChanged && !companyUpdateFailed;
      //  When both succeeded, the /company response carries it.
      if (domainSucceeded && !companySucceeded) {
        await refreshCompanyState(companyId);
      }

      return !domainEditFailed && !companyUpdateFailed;
    },
    [
      companyId,
      defaultValues,
      editDomain,
      isConsolidatedV2,
      isOnboarded,
      pushErrorToast,
      refreshCompanyState,
      store,
      updateCompanyAndState,
    ]
  );
}
